import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service, { inject } from '@ember/service';
import { set } from '@ember/object';
import { restartableTask, dropTask, timeout } from 'ember-concurrency';
import { beebeeAjaxGet } from '../lib/utility/ajax.js';
import '@ember/utils';
import { safeTimeZone } from '../lib/utility/dates.js';
import { setDefaultCustomField } from '../lib/utility/models.js';
import '../lib/utility/province.js';
import moment from 'moment-timezone';
import { notifyUserError } from '../lib/utility/misc.js';
import { singularize } from 'ember-inflector';
import getConfig from '../lib/get-config.js';

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7;

// ============================================================================
// CONSTANTS
// ============================================================================

/** UI stabilization delay (ms) */
const UI_STABILIZATION_DELAY = 200;

/** Default currency */
const DEFAULT_CURRENCY = 'EUR';

/** Default XML document type */
const DEFAULT_XML_DOCUMENT_TYPE = 'TD01: Fattura';

/** Default expense IVA rate */
const DEFAULT_EXPENSE_IVA_RATE = '22';

/** Models that carry a status */
const MODELS_WITH_STATUS = Object.freeze(['contact', 'product', 'project', 'workhour']);

/** Document-type -> default status */
const DOCUMENT_TYPE_STATUS_MAP = Object.freeze({
  ddt: 'active',
  receipt: 'unpaied',
  contract: 'open',
  default: 'draft'
});

/** Document types supporting the advanced option */
const DOCUMENT_TYPES_WITH_ADVANCED = Object.freeze(['estimate', 'contract']);

// ============================================================================
// SERVICE
// ============================================================================

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — Octane replacement for the Classic `new-model` mixin;
 *              creates new records (contact/invoice/event/expense/…) with the
 *              org's default fiscal, status and custom-field configuration
 *   extends:   Service (ember, base class)
 *   template:  none
 *   key:       createNewModel (@dropTask, main entry / mirrors mixin's showNew),
 *              showNew, completeTypeInvoice (@restartableTask), invNumber
 *              (@restartableTask), getDocumentType/getInvoiceLordo/
 *              getInvoiceAdvanced/getInvoiceUnit/getDefaultInvoiceFromId,
 *              private helpers: _configureModelStatus, _initializeModelRelations,
 *              _applyModelSpecificLogic, _openCreationModal
 *   uses:      store, intl, session, notify, indexContext, appState, modal
 *              services; lib/utility (setDefaultCustomField, beebeeAjaxGet,
 *              notifyUserError, safeTimeZone); ember-inflector (singularize);
 *              moment-timezone; lib/get-config
 *   siblings:  -
 *   tests:     new-record-factory-test.js, new-record-factory-wave4-test.js,
 *              new-record-factory-wave5-test.js, new-record-factory-wave6-test.js
 *   gotchas:   createNewModel is a @dropTask — a second call while one is
 *              running is silently dropped, not queued; catches its own errors
 *              via notifyUserError and returns null rather than rejecting
 *
 * Mixin-removal Phase 1.3 (scaffold — additive, no consumer switched yet). The
 * mixin reached `applicationController.{isClinic,gdprField,modalComponents}` by
 * name; the service routes those through the Phase-0 `app-state` / `modal`
 * services. It stays DOM-free: the old `zfDashNew.close()` +
 * `document.body.classList.remove('dropdown-open')` become an optional
 * `closeDropdown` callback the caller supplies. Document/status context that the
 * mixin read off the host component now comes from `indexContext.controller`.
 *
 * @class NewRecordFactoryService
 * @extends Service
 */
let NewRecordFactoryService = (_class = class NewRecordFactoryService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "session", _descriptor3, this);
    _initializerDefineProperty(this, "notify", _descriptor4, this);
    _initializerDefineProperty(this, "indexContext", _descriptor5, this);
    _initializerDefineProperty(this, "appState", _descriptor6, this);
    _initializerDefineProperty(this, "modal", _descriptor7, this);
  }
  // --------------------------------------------------------------------------
  // Invoice configuration helpers
  // --------------------------------------------------------------------------

  getDocumentType(defDocumentType) {
    return DOCUMENT_TYPE_STATUS_MAP[defDocumentType] || DOCUMENT_TYPE_STATUS_MAP.default;
  }
  getInvoiceLordo() {
    return this.session.option_field?.default_invoice_lordo || false;
  }
  getInvoiceAdvanced(defDocumentType) {
    if (DOCUMENT_TYPES_WITH_ADVANCED.includes(defDocumentType)) {
      return this.session.option_field?.default_estimate_advanced || false;
    }
    return false;
  }
  getDefaultInvoiceNouser() {
    return this.session.option_field?.default_invoice_nouser || false;
  }
  getInvoiceUnit() {
    const defaultUnit = this.appState.isClinic ? 'seduta' : 'pezzo';
    return this.session.option_field?.default_invoice_unit || defaultUnit;
  }
  getInvoiceLineDefault() {
    const name = this.session.option_field?.invoice_line_default || '';
    const regexClean = /{{\s*([^}]+)\s*}}/g;
    return name.replace(regexClean, '');
  }
  getInvoiceLineDefaultDescription() {
    return this.session.option_field?.invoice_line_default_description || '';
  }
  getDefaultInvoiceFromId() {
    const optionField = this.session.option_field;
    const defaultFromId = optionField?.default_invoice_from;
    const defaultInvoiceLists = optionField?.default_invoice_lists;
    const currentUserId = this.session.me?.id;
    const currentUserLists = defaultInvoiceLists?.[currentUserId];
    if (Array.isArray(currentUserLists)) {
      const defaultList = currentUserLists.find(list => list.default === true);
      if (defaultList) {
        return defaultList.id;
      }
    }
    return defaultFromId;
  }
  fetchDocumentTypeNumber(dataSend) {
    return beebeeAjaxGet(`${getConfig(this).APP.urlApi}/document_type_number`, this.session.token, dataSend);
  }

  // --------------------------------------------------------------------------
  // Private helpers
  // --------------------------------------------------------------------------

  _getCurrency() {
    return this.session.option_field?.intl?.currency || DEFAULT_CURRENCY;
  }
  _createAddressFromSource(sourceAddress) {
    if (sourceAddress) {
      const attributeNames = Array.from(sourceAddress.constructor.attributes.keys());
      const attributes = sourceAddress.getProperties(...attributeNames);
      return this.store.createRecord('address', attributes);
    }
    return this.store.createRecord('address', {});
  }
  _setInvoiceUserProperties(newModel, user) {
    newModel.setProperties({
      user,
      society_first_name: user.first_name,
      society_last_name: user.last_name,
      society_piva: user.piva,
      society_cf: user.cod_fiscale,
      society_piva_name: user.piva_name,
      fisica_giuridica: user.fisica_giuridica,
      society: user.ragione_sociale
    });
  }
  _setInvoiceBaseProperties(newModel, user, defDocumentType) {
    const invoiceSetting = user.user_invoice_setting;
    newModel.setProperties({
      lordo: this.getInvoiceLordo(),
      status: this.getDocumentType(defDocumentType),
      advanced: this.getInvoiceAdvanced(defDocumentType),
      xml_document_type: DEFAULT_XML_DOCUMENT_TYPE,
      document_type: defDocumentType,
      bank_name: invoiceSetting?.bank_name,
      bank_iban: invoiceSetting?.bank_iban,
      bank_bic: invoiceSetting?.bank_bic,
      rea: invoiceSetting?.rea,
      regime_fiscale: invoiceSetting?.regime_fiscale,
      notes: user.invoice_notes,
      ts_send: invoiceSetting?.ts_enable,
      ts_enable: invoiceSetting?.ts_enable,
      valuta: this._getCurrency()
    });
  }
  _createDefaultInvoiceProduct(user) {
    const product = this.store.createRecord('invoiceproduct', {
      name: this.getInvoiceLineDefault(),
      description: this.getInvoiceLineDefaultDescription(),
      price: null,
      unit: this.getInvoiceUnit(),
      cost: 0,
      qta: 1,
      rank: 1
    });
    if (user.user_taxes) {
      const ivaRate = user.user_taxes.find(u => u.is_iva === true);
      if (ivaRate) {
        product.setProperties({
          iva_rate: ivaRate.rate,
          iva_natura: ivaRate.iva_natura
        });
      }
      const hasActiveRitenuta = user.user_taxes.some(u => u.is_ritenuta === true && u.is_active_default);
      if (hasActiveRitenuta) {
        set(product, 'ritenuta', true);
      }
    }
    return product;
  }
  _configureRivalsaAndBollo(newModel, user) {
    if (!user.user_taxes) return;
    const hasActiveRivalsa = user.user_taxes.some(u => u.is_rivalsa === true && u.is_active_default);
    if (hasActiveRivalsa) {
      set(newModel, 'rivalsa', true);
    }
    const bollo = user.user_taxes.find(u => u.is_bollo === true);
    if (bollo) {
      newModel.setProperties({
        bollo: true,
        bollo_cliente: bollo.bollo_cliente,
        bollo_virtuale: bollo.bollo_virtuale
      });
    }
  }
  *_configureInvoiceStyle(newModel, user) {
    if (!user.invoice_styles) return;
    const invoiceStyles = yield user.invoice_styles;
    const defaultStyle = invoiceStyles.find(u => u.is_default === true);
    if (defaultStyle) {
      set(newModel, 'invoice_style', defaultStyle);
    }
  }
  _buildModalTitle(modal, defDocumentType) {
    const organization = this.session.me;
    const newLabel = this.intl.t('new');
    let entityName;
    if (modal === 'invoice' && defDocumentType) {
      entityName = organization.get(`organization.${defDocumentType}_name`);
    } else if (modal === 'invoice') {
      entityName = this.indexContext.controller?.model?.modelName;
    } else {
      entityName = organization.get(`organization.${modal}_name`);
    }
    return `${newLabel} ${entityName}`;
  }

  // --------------------------------------------------------------------------
  // Tasks
  // --------------------------------------------------------------------------

  *completeTypeInvoice(newModel, defDocumentTypeArgs) {
    const meId = this.session.me.id;
    let user = this.store.peekRecord('contact', meId);
    const defaultFromId = this.getDefaultInvoiceFromId();
    if (defaultFromId) {
      user = this.store.peekRecord('contact', defaultFromId);
      const needsReload = !user?.user_invoice_setting?.regime_fiscale || !user?.addresses?.[0]?.id;
      if (needsReload) {
        user = yield this.store.findRecord('contact', defaultFromId, {
          reload: true
        });
      }
    }
    const defDocumentType = defDocumentTypeArgs || this.indexContext.controller?.model?.documentType;
    if (!this.getDefaultInvoiceNouser()) {
      this._setInvoiceUserProperties(newModel, user);
    }
    this._setInvoiceBaseProperties(newModel, user, defDocumentType);
    const product = this._createDefaultInvoiceProduct(user);
    this._configureRivalsaAndBollo(newModel, user);
    yield* this._configureInvoiceStyle(newModel, user);
    newModel.invoiceproducts.push(product);
    const sourceAddress = user.addresses?.find(a => a.is_invoice === true) || user.addresses?.[0];
    const societyAddress = this._createAddressFromSource(sourceAddress);
    set(newModel, 'society_address', societyAddress);
  }
  *invNumber(newInvoice, defDocumentType) {
    const defaultFromId = this.getDefaultInvoiceFromId();
    const dataSend = {
      user_id: defaultFromId,
      document_type: defDocumentType || this.indexContext.controller?.model?.documentType
    };
    const data = yield this.fetchDocumentTypeNumber(dataSend);
    if (!Array.isArray(data) || data.length === 0) return;
    if (data.length > 1) {
      set(newInvoice, 'sezionali', data);
    }
    const defaultSezionale = data.find(i => i.is_default === true) || data[0];
    set(newInvoice, 'sezionale', defaultSezionale);
    set(newInvoice, 'draft_number', defaultSezionale.number || 1);
  }

  /**
   * Create a new record with the org's default configuration.
   * @param {string|null} defModal - model name (defaults to the index model)
   * @param {string|null} defDocumentType
   * @param {Object|null} defStatus
   * @param {boolean} openModal - open the edit modal afterwards
   * @param {Function|null} closeDropdown - optional caller callback to close the
   *   "new" dropdown (replaces the mixin's DOM-coupled `zfDashNew.close()` +
   *   `document.body.classList.remove('dropdown-open')`).
   * @returns {Model|null}
   */
  *createNewModel(defModal = null, defDocumentType = null, defStatus = null, openModal = true, closeDropdown = null) {
    try {
      const modal = defModal || singularize(this.indexContext.controller?.model?.modelName);
      const me = this.store.peekRecord('contact', this.session.me.id);
      const timezone = safeTimeZone(me.time_zone);
      const newModel = this.store.createRecord(modal, {
        start_date: moment().tz(timezone, true).startOf('hour').format('YYYY-MM-DDTHH:mmZ'),
        end_date: moment().tz(timezone, true).add(1, 'hours').startOf('hour').format('YYYY-MM-DDTHH:mmZ'),
        date: moment().tz(timezone, true).format('YYYY-MM-DDTHH:mmZ'),
        sent: false,
        success: true
      });
      yield* this._configureModelStatus(newModel, modal, defStatus);
      yield setDefaultCustomField(me, newModel, this.store);
      yield* this._initializeModelRelations(newModel, me);
      yield* this._applyModelSpecificLogic(newModel, modal, defDocumentType, me);
      if (closeDropdown) {
        closeDropdown();
      }
      if (openModal) {
        this._openCreationModal(newModel, modal, defDocumentType);
      }
      return newModel;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return null;
    }
  }
  *_configureModelStatus(newModel, modal, defStatus) {
    const status = this.indexContext.controller?.status;
    if (defStatus) {
      const defStatusId = defStatus?.id || defStatus;
      const storeStatus = this.store.peekRecord(`${modal}_state`, defStatusId);
      if (storeStatus) {
        set(newModel, `${modal}_state`, storeStatus);
      }
      yield timeout(UI_STABILIZATION_DELAY);
    } else if (status && MODELS_WITH_STATUS.includes(modal)) {
      const resolvedStatus = typeof status === 'object' ? status : this.store.peekRecord(`${modal}_state`, status);
      if (resolvedStatus) {
        set(newModel, `${modal}_state`, resolvedStatus);
      }
    }
  }
  *_initializeModelRelations(newModel, me) {
    const contacts = yield newModel.contacts;
    const addresses = yield newModel.addresses;

    // dual receiver — resolved hasMany may pass Array.isArray in modern ED; native push works for both
    if (Array.isArray(contacts)) {
      contacts.push(me);
    }
    if (Array.isArray(addresses)) {
      addresses.push(this.store.createRecord('address', {
        is_invoice: true
      }));
    }
  }
  *_applyModelSpecificLogic(newModel, modal, defDocumentType, me) {
    switch (modal) {
      case 'invoice':
        set(newModel, 'date', moment().format('YYYY-MM-DD'));
        yield this.completeTypeInvoice.perform(newModel, defDocumentType);
        if (!this.getDefaultInvoiceNouser()) {
          yield this.invNumber.perform(newModel, defDocumentType);
        }
        break;
      case 'expense':
        {
          set(newModel, 'valuta', this._getCurrency());
          set(newModel, 'date', moment().format('YYYY-MM-DD'));
          const newArticle = this.store.createRecord('expensearticle', {
            first_rate: DEFAULT_EXPENSE_IVA_RATE,
            first_rate_included: true
          });
          newModel.expensearticles.push(newArticle);
          break;
        }
      case 'event':
        this._configureEventColor(newModel, me);
        break;
      case 'contact':
        this._initializeContactConsents(newModel);
        break;
    }
  }
  _configureEventColor(newModel, me) {
    const calendarSettings = this.session.option_field?.calendar_settings;
    const useSpecialistColor = calendarSettings?.default_color === 'specialista';
    if (useSpecialistColor && me.color) {
      set(newModel, 'color', me.color);
    }
  }
  _initializeContactConsents(newModel) {
    const termOptions = this.appState.gdprField?.organization_term_options?.data;
    if (!Array.isArray(termOptions)) return;
    termOptions.forEach(consent => {
      const consentRecord = this.store.createRecord('contact-consensi', {
        option_name: consent.name,
        accepted: null
      });
      newModel.contact_consensis.push(consentRecord);
    });
  }

  /**
   * Push the creation modal onto the stack via the `modal` service (DOM-free —
   * the dropdown-class toggling the mixin did here is now the caller's job).
   */
  _openCreationModal(newModel, modal, defDocumentType) {
    const modalConfig = {
      title: this._buildModalTitle(modal, defDocumentType),
      componentName: this.session.me.get(`role.application.${modal}_edit`),
      item: newModel
    };
    this.modal.open(modalConfig);
    // Cosmetic dropdown close the mixin did here (DOM-global). The component's
    // own `zfDashNew.close()` is still the caller's job via `closeDropdown`.
    if (typeof document !== 'undefined') {
      document.body.classList.remove('dropdown-open');
    }
  }

  /**
   * Show the modal to create a new item. Mirrors the mixin's `showNew` action.
   */
  showNew(defModal = null, defDocumentType = null) {
    return this.createNewModel.perform(defModal, defDocumentType);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "indexContext", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "appState", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "modal", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "completeTypeInvoice", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "completeTypeInvoice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "invNumber", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "invNumber"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createNewModel", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "createNewModel"), _class.prototype), _class);

export { NewRecordFactoryService as default };
//# sourceMappingURL=new-record-factory.js.map
